import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import InputMask from "react-input-mask";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleInfo, faPen, faPaperPlane, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import NotyfContext from "../contexts/NotyfContext.js";
import { disconnectSystemWhatsAppSender, synchronizeSystemWhatsAppSender } from "../api/whatsappSystem.js";
import WhatsAppGlobalTemplatesCard from "./WhatsAppGlobalTemplatesCard.js";
import { isDeletedWhatsAppTemplate } from "../utils/whatsappTemplatePresentation.js";

const PATH = "/api/system/settings/whatsapp";
// 250 is Meta's starting daily limit; the server also caps the value at the limit Meta reports.
const DEFAULT_CAPACITY_LIMIT = "250";
const MAX_CAPACITY_LIMIT = 100000;
const WEBHOOK_CHECK_MESSAGES = {
  WHATSAPP_WEBHOOK_APP_SUBSCRIPTION_MISSING: "O app da Meta não tem webhook do WhatsApp configurado.",
  WHATSAPP_WEBHOOK_SUBSCRIPTION_INACTIVE: "O webhook do WhatsApp está inativo no app da Meta.",
  WHATSAPP_WEBHOOK_CALLBACK_MISMATCH: "A URL de callback no app da Meta não aponta para este servidor (/api/webhooks/whatsapp).",
  WHATSAPP_WEBHOOK_MESSAGES_FIELD_MISSING: "O campo messages não está assinado no webhook do app da Meta.",
  WHATSAPP_WEBHOOK_CHECK_FAILED: "Não foi possível consultar o webhook no app da Meta.",
};
const emptyForm = () => ({ waba_id: "", phone_number_id: "", access_token: "", capacity_limit: DEFAULT_CAPACITY_LIMIT });
const senderLabel = (sender) => [sender?.display_name, sender?.display_phone].filter(Boolean).join(" · ");
const templateVariables = (template) => {
  const names = new Set();
  [template?.components?.header?.text, template?.components?.body?.text].forEach((part) => {
    String(part || "").replace(/\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g, (_, name) => {
      names.add(name);
      return _;
    });
  });
  return [...names];
};
const testVariableExamples = {
  nome: "Mariana",
  evento: "Seu evento",
  event_name: "Seu evento",
  email: "mariana@exemplo.com",
  user_id: "123",
  user_access_code: "ABC123",
  rsvp_link: "https://evento.exemplo/confirmacao",
  qrcode_with_user_access_code_url: "https://evento.exemplo/qrcode/123",
  qrcode_download_url: "https://evento.exemplo/qrcode/123.png",
};
const testVariablesForTemplate = (template) => Object.fromEntries(
  templateVariables(template).map((name) => [
    name,
    String(template?.components?.sample_values?.[name] || "").trim()
      || testVariableExamples[name]
      || name.split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" "),
  ]),
);
const canTestTemplate = (template, sender) => (
  (template?.account_id != null && sender?.account_id != null
    ? String(template.account_id) === String(sender.account_id)
    : template?.waba_id && sender?.waba_id
      ? String(template.waba_id) === String(sender.waba_id)
      : String(template?.sender_id) === String(sender?.sender_id))
  && template.sendable === true
  && !isDeletedWhatsAppTemplate(template)
  && template.archived !== true
  && template.configuration_status === "ready"
  && template.is_available !== false
  && template.is_supported !== false
  && template.status === "approved"
  && template.provider_status === "approved"
  && Boolean(template.provider_template_name || template.meta_template_name)
  && template.purpose !== "rsvp"
  && !JSON.stringify(template.components || {}).includes("rsvp_link")
);
const previewText = (value, variables) => String(value || "").replace(
  /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g,
  (placeholder, name) => variables[name]?.trim() || placeholder,
);

const errorMessage = (error) => {
  const messages = {
    WHATSAPP_SYSTEM_OPERATION_FAILED: "Não foi possível concluir a operação de WhatsApp. Tente novamente.",
    WHATSAPP_R1_CONFIGURATION_INVALID: "Informe os IDs numéricos da conta e do número, além de um token válido da Cloud API.",
    WHATSAPP_META_APP_NOT_CONFIGURED: "Configure o ID e o segredo do aplicativo Meta no servidor.",
    WHATSAPP_GRAPH_API_NOT_CONFIGURED: "A versão da API Graph da Meta não está configurada no servidor.",
    WHATSAPP_META_ACCESS_TOKEN_REQUIRED: "Informe o token de acesso da Cloud API.",
    WHATSAPP_META_TOKEN_INVALID: "O token não pertence ao aplicativo configurado ou não está válido.",
    WHATSAPP_META_MESSAGING_PERMISSION_REQUIRED: "O token precisa da permissão whatsapp_business_messaging.",
    WHATSAPP_META_MANAGEMENT_PERMISSION_REQUIRED: "O token precisa da permissão whatsapp_business_management.",
    WHATSAPP_META_PERMISSION_MISSING: "A Meta recusou uma permissão necessária. Confira o acesso do token e tente novamente.",
    WHATSAPP_META_AUTHORIZATION_FAILED: "A Meta recusou o token. Gere ou informe um token válido e tente novamente.",
    WHATSAPP_META_RATE_LIMITED: "A Meta limitou temporariamente as solicitações. Aguarde um pouco e tente novamente.",
    WHATSAPP_META_REQUEST_FAILED: "Não foi possível consultar a Meta. Tente novamente.",
    WHATSAPP_META_DISCOVERY_LIMIT_EXCEEDED: "A Meta retornou muitos resultados para concluir a validação. Tente novamente.",
    WHATSAPP_R1_WABA_NOT_AUTHORIZED: "O token não tem acesso à conta WhatsApp informada.",
    WHATSAPP_R1_PHONE_NOT_IN_WABA: "O número informado não pertence à conta WhatsApp selecionada.",
    WHATSAPP_R1_PHONE_NOT_REGISTERED: "Registre o número na Cloud API antes de ativá-lo no painel.",
    WHATSAPP_R1_PHONE_QUALITY_BLOCKED: "A qualidade do número está bloqueada na Meta. Verifique o WhatsApp Manager antes de ativá-lo.",
    WHATSAPP_R1_CAPACITY_INVALID: "Informe um limite operacional diário válido para ativar o número.",
    WHATSAPP_R1_PHONE_OWNERSHIP_CONFLICT: "Este número já pertence a outro remetente cadastrado.",
    WHATSAPP_R1_CONFIGURATION_BUSY: "Outra operação de configuração deste número está em andamento. Aguarde e tente novamente.",
    WHATSAPP_R1_CREDENTIAL_CHANGED: "A credencial mudou durante a atualização. Recarregue a integração atual e tente novamente.",
    WHATSAPP_SENDER_META_IDENTIFIERS_MISSING: "Faltam identificadores Meta para este número. Atualize a configuração do remetente.",
    WHATSAPP_CREDENTIAL_STORE_NOT_CONFIGURED: "A criptografia das credenciais precisa ser configurada no servidor.",
    WHATSAPP_SENDER_CREDENTIAL_INVALID: "O token informado não é válido para armazenamento. Confira o valor e tente novamente.",
    WHATSAPP_SENDER_CREDENTIAL_UNREADABLE: "Não foi possível ler a credencial criptografada. Verifique a configuração de criptografia no servidor.",
    WHATSAPP_SENDER_CREDENTIAL_EXPIRED: "O token do remetente expirou. Atualize o número com um token válido.",
    WHATSAPP_SENDER_CREDENTIAL_NOT_CONFIGURED: "O token do remetente não está configurado. Atualize o número com um token válido.",
    WHATSAPP_SENDER_NOT_FOUND: "O remetente R1 não foi encontrado. Atualize a página e tente novamente.",
    WHATSAPP_SENDER_DISCONNECTED: "Este remetente está desconectado. Configure novamente o número R1.",
    WHATSAPP_SENDER_SYNC_FAILED: "Não foi possível sincronizar o número com a Meta. Tente novamente.",
    WHATSAPP_SENDER_SYNC_STALE: "A integração mudou durante a sincronização. Atualize a página e tente novamente.",
    WHATSAPP_SENDER_SYNC_IN_PROGRESS: "Já existe uma sincronização deste número em andamento. Aguarde a conclusão e tente novamente.",
    WHATSAPP_WEBHOOK_SUBSCRIPTION_FAILED: "Não foi possível atualizar a inscrição do webhook na Meta.",
    WHATSAPP_SENDER_UNAVAILABLE: "Configure e ative o número R1 antes de enviar a mensagem de teste.",
    WHATSAPP_PROVIDER_NOT_CONFIGURED: "O envio pela Cloud API está desativado no servidor.",
    WHATSAPP_TEST_RECIPIENT_INVALID: "Informe o destino com DDI 55, DDD e número, sem o zero inicial.",
    WHATSAPP_TEST_TEMPLATE_REQUIRED: "Escolha um modelo aprovado para enviar o teste.",
    WHATSAPP_TEMPLATE_NOT_FOUND: "O modelo selecionado não pertence à conta WhatsApp da R1. Sincronize a lista e escolha outro.",
    WHATSAPP_TEMPLATE_NOT_APPROVED: "O modelo ainda não está aprovado na Meta. Sincronize o status e escolha um modelo aprovado.",
    WHATSAPP_TEMPLATE_UNAVAILABLE: "A Meta não disponibiliza mais este modelo. Sincronize o catálogo antes de tentar novamente.",
    WHATSAPP_TEMPLATE_CONFIGURATION_REQUIRED: "Complete a configuração local do modelo antes de usá-lo.",
    WHATSAPP_TEMPLATE_UNSUPPORTED: "Este formato de modelo ainda não pode ser enviado pela integração R1.",
    WHATSAPP_TEMPLATE_DELETE_IN_PROGRESS: "A exclusão deste modelo na Meta ainda está em andamento. Sincronize o catálogo antes de repetir a ação.",
    WHATSAPP_TEMPLATE_ALREADY_CONFIGURED: "Este modelo da Meta já está configurado no painel. Atualize a lista para abrir a configuração existente.",
    WHATSAPP_TEST_TEMPLATE_RSVP_UNSUPPORTED: "Modelos de RSVP exigem um convite de evento. Escolha outro modelo para este teste.",
    WHATSAPP_TEST_TEMPLATE_UNAVAILABLE: "O modelo aprovado não tem os dados necessários para envio. Sincronize-o com a Meta.",
    WHATSAPP_TEST_TEMPLATE_VARIABLES_INVALID: "Preencha todos os campos do modelo para enviar o teste.",
    WHATSAPP_TEST_MESSAGE_FAILED: "A Meta recusou a mensagem de teste. Confira o código e a referência Meta abaixo.",
    WHATSAPP_TEST_SUBMISSION_UNCERTAIN: "A Meta não confirmou o envio. Confira seu WhatsApp antes de tentar novamente.",
  };
  const code = typeof error === "string" ? error : error?.response?.data?.code;
  const responseMessage = error?.response?.data?.message;
  const fallbackMessage = typeof error === "string" ? error : error?.message;
  const usableMessage = [responseMessage, fallbackMessage].find((message) => (
    message && message !== code && !/^[A-Z][A-Z0-9_]+$/.test(message)
  ));
  return messages[code] || usableMessage || "Não foi possível configurar o WhatsApp.";
};

const WhatsAppFeedbackAlert = ({ variant = "info", className = "", children }) => (
  <Alert variant={variant} className={`p-2 ${className}`.trim()}>
    <FontAwesomeIcon
      icon={variant === "success" ? faCircleCheck : variant === "info" ? faCircleInfo : faTriangleExclamation}
      className="me-2"
    />
    {children}
  </Alert>
);

const WhatsAppSystemSettingsCard = ({ onRegisterLoader, onRefresh }) => {
  const notyf = React.useContext(NotyfContext);
  const [configuration, setConfiguration] = useState({ senders: [], infrastructure: null });
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openingForm, setOpeningForm] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [testTemplates, setTestTemplates] = useState([]);
  const [testTemplateId, setTestTemplateId] = useState("");
  const [testVariables, setTestVariables] = useState({});
  const [showTestModal, setShowTestModal] = useState(false);
  const [testModalFromTemplateEditor, setTestModalFromTemplateEditor] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState("");
  const [testError, setTestError] = useState("");
  const [testResult, setTestResult] = useState(null);

  // Loading is read-only. The Meta sync runs only when the System Settings page
  // passes synchronize after the operator confirms a manual refresh, because a
  // failed or degraded sync suspends the sender and pauses its campaigns.
  const load = useCallback(async ({ synchronize = false } = {}) => {
    try {
      let response = await axios.get(PATH);
      let result = response.data || {};
      const refreshSender = (Array.isArray(result.senders) ? result.senders : []).find((item) => item.is_default);
      if (synchronize && refreshSender?.sender_id && result.infrastructure?.api_enabled !== false) {
        try {
          await synchronizeSystemWhatsAppSender(refreshSender.sender_id, {
            subscribeWebhook: result.infrastructure?.webhook_configured === true,
          });
        } catch (_) {
          // Keep the last known configuration visible; the sender's sync error is
          // shown by the next successful configuration read.
        }
        response = await axios.get(PATH);
        result = response.data || {};
      }
      setConfiguration({
        senders: Array.isArray(result.senders) ? result.senders : [],
        infrastructure: result.infrastructure || null,
      });
      setLoadError(false);
      setError("");
      const activeSender = (Array.isArray(result.senders) ? result.senders : []).find((item) => item.is_default);
      const apiEnabled = result.infrastructure?.api_enabled !== false;
      return { ready: activeSender?.status === "active" && apiEnabled, hasSender: Boolean(activeSender?.sender_id) && apiEnabled };
    } catch (requestError) {
      setLoadError(true);
      setError(errorMessage(requestError));
      return { ready: false, hasSender: false };
    }
  }, []);

  useEffect(() => {
    if (onRegisterLoader) return onRegisterLoader("whatsapp", load);
    load();
  }, [load, onRegisterLoader]);

  const sender = configuration.senders.find((item) => item.is_default) || null;
  const infrastructure = configuration.infrastructure;
  const ready = sender?.status === "active" && sender?.credential_status === "configured";
  const campaignReady = sender?.campaign_ready === true;
  const setupAlerts = [];
  if (sender?.last_sync_error) {
    setupAlerts.push({ variant: "warning", message: `Última sincronização: ${errorMessage(sender.last_sync_error)}` });
  }
  if (infrastructure) {
    if (infrastructure.api_enabled === false) {
      setupAlerts.push({ variant: "warning", message: "O envio pela Cloud API está desativado no servidor." });
    }
    if (!infrastructure.meta_app_configured) {
      setupAlerts.push({ variant: "warning", message: "Configure WHATSAPP_APP_ID e WHATSAPP_APP_SECRET no servidor." });
    }
    if (!infrastructure.credential_store_configured) {
      setupAlerts.push({ variant: "warning", message: "A criptografia das credenciais precisa ser configurada no servidor." });
    }
    if (ready && !campaignReady) {
      setupAlerts.push({
        variant: "info",
        message: `O número pode ser usado no teste de saída, mas campanhas e acompanhamento de entregas dependem da inscrição no webhook e da configuração do token de verificação e segredo Meta.${!infrastructure.webhook_configured ? " Configure esses dados no servidor." : " Atualize a configuração para concluir a inscrição."}`,
      });
    }
    if (sender?.webhook_check_error) {
      setupAlerts.push({ variant: "warning", message: `${WEBHOOK_CHECK_MESSAGES[sender.webhook_check_error] || "A verificação do webhook na Meta falhou."} Novas campanhas de RSVP ficam bloqueadas e os envios de RSVP aguardam até a próxima verificação sem erro.` });
    }
    if (sender?.webhook_silent_since) {
      setupAlerts.push({ variant: "warning", message: `A Meta não está entregando notificações deste número desde ${new Date(sender.webhook_silent_since).toLocaleString("pt-BR")}. Os envios de RSVP aguardam e voltam sozinhos quando as notificações retornarem.` });
    }
    if (!infrastructure.phone_hash_secret_configured) {
      setupAlerts.push({ variant: "info", message: "O segredo dos números dos destinatários será necessário para campanhas, mas não para este teste." });
    }
  }
  const availableTestTemplates = testTemplates.filter((template) => canTestTemplate(template, sender));
  const testableTemplateIds = ready && infrastructure?.api_enabled !== false
    ? availableTestTemplates.map((template) => template.template_id)
    : [];
  const selectedTestTemplate = availableTestTemplates.find((template) => String(template.template_id) === testTemplateId);
  const updateForm = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  const openForm = async () => {
    setOpeningForm(true);
    setError("");
    setNotice("");
    try {
      setForm({
        waba_id: sender?.waba_id || "",
        phone_number_id: sender?.phone_number_id || "",
        access_token: "",
        capacity_limit: sender?.capacity?.capacity_limit ? String(sender.capacity.capacity_limit)
          : sender?.capacity?.meta_messaging_limit ? String(sender.capacity.meta_messaging_limit) : DEFAULT_CAPACITY_LIMIT,
      });
      setShowForm(true);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setOpeningForm(false);
    }
  };

  const closeForm = () => {
    setForm(emptyForm());
    setError("");
    setShowRemoveConfirmation(false);
    setShowForm(false);
  };

  const refreshSettings = () => (onRefresh ? onRefresh() : load());

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await axios.post(`${PATH}/r1-sender`, {
        waba_id: form.waba_id.trim(),
        phone_number_id: form.phone_number_id.trim(),
        access_token: form.access_token.trim(),
        capacity_limit: Number(form.capacity_limit),
      });
      closeForm();
      setTestResult(null);
      setTestError("");
      setNotice(sender ? "Número R1 atualizado e sincronizado com a Meta." : "Número R1 configurado e sincronizado com a Meta.");
      notyf?.open({ type: "success", message: sender ? "Número R1 atualizado." : "Número R1 configurado.", ripple: true, dismissible: true });
      await refreshSettings();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  const removeIntegration = async () => {
    if (!sender?.sender_id) return;
    setRemoving(true);
    setRemoveError("");
    try {
      const response = await disconnectSystemWhatsAppSender(sender.sender_id);
      const result = response.data?.configuration || {};
      setConfiguration({
        senders: Array.isArray(result.senders) ? result.senders : [],
        infrastructure: result.infrastructure || null,
      });
      setForm(emptyForm());
      setError("");
      setTestResult(null);
      setTestError("");
      setTestTemplateId("");
      setTestVariables({});
      setNotice("Integração removida. Cadastre um novo número R1 para continuar.");
      setShowRemoveConfirmation(false);
      await refreshSettings();
    } catch (requestError) {
      setRemoveError(errorMessage(requestError));
    } finally {
      setRemoving(false);
    }
  };

  const sendTest = async (event) => {
    event.preventDefault();
    if (!selectedTestTemplate) {
      setTestError("Escolha um modelo aprovado para enviar o teste.");
      return;
    }
    setTestBusy(true);
    setTestError("");
    setTestResult(null);
    try {
      const response = await axios.post(`${PATH}/test-message`, {
        recipient_phone: testRecipient.replace(/\D/g, ""),
        template_id: selectedTestTemplate.template_id,
        variables: testVariablesForTemplate(selectedTestTemplate),
      });
      setTestResult(response.data || null);
    } catch (requestError) {
      const details = requestError?.response?.data?.details;
      const code = requestError?.response?.data?.code;
      const uncertain = code === "WHATSAPP_TEST_MESSAGE_FAILED" && (!details?.httpStatus || details.httpStatus >= 500);
      const providerCode = Number(details?.providerCode);
      const message = providerCode === 131058
          ? "O servidor ainda tentou usar o modelo hello_world, permitido apenas para números de teste. Reinicie a API para carregar a correção."
          : uncertain
            ? "A Meta não confirmou o resultado. Confira seu WhatsApp antes de tentar novamente."
            : errorMessage(requestError);
      const provider = details?.providerCode ? ` Código Meta: ${details.providerCode}${details.providerSubcode ? `/${details.providerSubcode}` : ""}.` : "";
      const reference = details?.fbtraceId ? ` Referência Meta: ${details.fbtraceId}.` : "";
      setTestError(`${message}${provider}${reference}`);
    } finally {
      setTestBusy(false);
    }
  };

  const requestTemplateTest = (template) => {
    const approvedTemplate = availableTestTemplates.find((item) => String(item.template_id) === String(template?.template_id));
    if (!ready || infrastructure?.api_enabled === false || !approvedTemplate) return;
    setTestTemplateId(String(approvedTemplate.template_id));
    setTestVariables(testVariablesForTemplate(approvedTemplate));
    setTestError("");
    setTestResult(null);
    setTestModalFromTemplateEditor(true);
    setShowTestModal(true);
  };

  const closeTestModal = () => {
    if (testBusy) return;
    setShowTestModal(false);
    setTestModalFromTemplateEditor(false);
  };

  return (
    <>
    <div className="rounded p-2" style={{ flex: "1 0 320px", backgroundColor: "#f4f5f7", border: "1px solid #e2e4e9" }}>
      <div className="d-flex align-items-center gap-2 px-2 py-2 mb-2">
        <FontAwesomeIcon icon={faWhatsapp} className="text-success" />
        <h6 className="mb-0 fw-bold text-dark">WhatsApp</h6>
      </div>
      <div className="d-flex flex-column gap-3">
        <Card className="border shadow-sm" style={{ marginBottom: 0 }}>
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
              <div>
                <h6 className="fw-bold text-dark mb-1">Número R1 da Cloud API</h6>
                <p className="text-muted small mb-0">Este número pode ser escolhido em qualquer evento.</p>
              </div>
              <Badge bg={ready ? "success" : sender?.status === "suspended" ? "danger" : "warning"}>
                {ready ? "Ativo" : sender?.status === "suspended" ? "Suspenso" : "Pendente"}
              </Badge>
            </div>
            {loadError ? (
              <WhatsAppFeedbackAlert variant="danger" className="mb-0">{error || "Não foi possível carregar a configuração."}</WhatsAppFeedbackAlert>
            ) : (
              <>
                {sender ? (
        <div className="border-top py-2 my-2">
                    <div className="d-flex flex-column gap-1 small">
                      <div className="d-flex justify-content-between gap-2 text-muted"><span>Remetente</span><strong className="text-dark text-end">{senderLabel(sender)}</strong></div>
                      <div className="d-flex justify-content-between gap-2 text-muted"><span>Conta WhatsApp</span><strong className="text-dark text-end">{sender.waba_id}</strong></div>
                      <div className="d-flex justify-content-between gap-2 text-muted"><span>Limite operacional</span><strong className="text-dark text-end">{sender.capacity?.effective_limit || sender.capacity?.capacity_limit || "não definido"} contatos por dia</strong></div>
                      {sender.capacity?.meta_messaging_limit && <div className="d-flex justify-content-between gap-2 text-muted"><span>Limite da Meta</span><strong className="text-dark text-end">{sender.capacity.meta_messaging_limit} contatos por dia</strong></div>}
                      <div className="d-flex justify-content-between gap-2 text-muted"><span>Último webhook recebido</span><strong className="text-dark text-end">{sender.webhook_last_received_at ? new Date(sender.webhook_last_received_at).toLocaleString("pt-BR") : "nenhum ainda"}</strong></div>
                      {sender.credential_expires_at && <div className="d-flex justify-content-between gap-2 text-muted"><span>Token válido até</span><strong className="text-dark text-end">{new Date(sender.credential_expires_at).toLocaleString("pt-BR")}</strong></div>}
                    </div>
                  </div>
                ) : <div className="small mb-2">Nenhum número R1 configurado.</div>}
                {setupAlerts.length > 0 && <WhatsAppFeedbackAlert variant={setupAlerts.some((item) => item.variant === "warning") ? "warning" : "info"} className="mb-2">
                  {setupAlerts.map((item) => item.message).join(" ")}
                </WhatsAppFeedbackAlert>}
                {notice && <WhatsAppFeedbackAlert variant="success" className="mt-3 mb-0">{notice}</WhatsAppFeedbackAlert>}
                {error && !loadError && !showForm && <WhatsAppFeedbackAlert variant="danger" className="mt-3 mb-0">{error}</WhatsAppFeedbackAlert>}
                <div className="d-flex justify-content-end align-items-center mt-3 pt-2 border-top">
                <Button size="sm" variant="primary" className="py-1 px-3" onClick={openForm} disabled={busy || testBusy || openingForm}>
                  {openingForm ? <><Spinner animation="border" size="sm" className="me-2" />Carregando...</> : <><FontAwesomeIcon icon={faPen} className="me-1" />{sender ? "Editar" : "Configurar número R1"}</>}
                </Button>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
        <WhatsAppGlobalTemplatesCard
          canLoad={!loadError}
          onTemplatesChange={setTestTemplates}
          onRegisterLoader={onRegisterLoader}
          onRefresh={onRefresh}
          testableTemplateIds={testableTemplateIds}
          onRequestTemplateTest={requestTemplateTest}
        />
      </div>
    </div>
    <Modal
      show={showForm}
      onHide={() => { if (!busy && !removing) closeForm(); }}
      fullscreen
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton={!busy && !removing}>
        <Modal.Title>
          {sender ? "Editar número R1 da Cloud API" : "Configurar número R1 da Cloud API"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3 p-md-4">
        <Form id="whatsapp-r1-sender-form" onSubmit={save}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>ID da conta WhatsApp</Form.Label>
                <Form.Control required pattern="[0-9]+" value={form.waba_id} onChange={(event) => updateForm("waba_id", event.target.value)} disabled={busy || Boolean(sender) || removing} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>ID do número</Form.Label>
                <Form.Control required pattern="[0-9]+" value={form.phone_number_id} onChange={(event) => updateForm("phone_number_id", event.target.value)} disabled={busy || Boolean(sender) || removing} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Limite diário para campanhas</Form.Label>
                <Form.Control required type="number" min="1" max={MAX_CAPACITY_LIMIT} value={form.capacity_limit} onChange={(event) => updateForm("capacity_limit", event.target.value)} disabled={busy || removing} />
                <Form.Text muted>
                  {sender?.capacity?.meta_messaging_limit
                    ? `A Meta permite ${sender.capacity.meta_messaging_limit} contatos por dia para este número; vale o menor dos dois valores.`
                    : "O servidor também respeita o limite diário informado pela Meta."}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Token</Form.Label>
                <Form.Control required={!sender} type={sender ? "text" : "password"} autoComplete="new-password"
                  value={sender ? "********" : form.access_token}
                  onChange={(event) => updateForm("access_token", event.target.value)}
                  disabled={busy || Boolean(sender) || removing} />
              </Form.Group>
            </Col>
          </Row>
        </Form>
        {notice && <WhatsAppFeedbackAlert variant="success" className="mt-3 mb-0">{notice}</WhatsAppFeedbackAlert>}
        {ready && (
          <div className="d-flex justify-content-end border-top mt-4 pt-3">
            <Button size="sm" variant="primary" className="py-1 px-3" onClick={() => { setTestModalFromTemplateEditor(false); setShowTestModal(true); }} disabled={busy || testBusy || removing}>
              <FontAwesomeIcon icon={faPaperPlane} className="me-1" />Enviar mensagem de teste
            </Button>
          </div>
        )}
        {error && <WhatsAppFeedbackAlert variant="danger" className="mt-3 mb-0">{error}</WhatsAppFeedbackAlert>}
      </Modal.Body>
      <Modal.Footer>
        {sender && <Button variant="danger" onClick={() => { setRemoveError(""); setShowRemoveConfirmation(true); }} disabled={busy || removing}>Remover integração</Button>}
        <Button type="submit" form="whatsapp-r1-sender-form" variant="primary" disabled={busy || removing}>
          {busy ? <><Spinner animation="border" size="sm" className="me-2" />Salvando...</> : sender ? "Salvar alterações" : "Validar e salvar"}
        </Button>
      </Modal.Footer>
    </Modal>
    <Modal
      show={showRemoveConfirmation}
      onHide={() => { if (!removing) setShowRemoveConfirmation(false); }}
      centered
      backdrop={removing ? "static" : true}
      keyboard={!removing}
    >
      <Modal.Header closeButton={!removing}>
        <Modal.Title>Remover integração do WhatsApp?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-0">A credencial será revogada, a seleção padrão será removida e as campanhas agendadas ou em andamento serão pausadas. A lista importada da Meta será limpa e poderá ser reconstruída ao reconectar. Rascunhos locais, configurações, modelos usados por campanhas e todo o histórico serão preservados. Nenhuma campanha será retomada automaticamente após a reconexão.</p>
        {removeError && <WhatsAppFeedbackAlert variant="danger" className="mt-3 mb-0">{removeError}</WhatsAppFeedbackAlert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowRemoveConfirmation(false)} disabled={removing}>Cancelar</Button>
        <Button variant="danger" onClick={removeIntegration} disabled={removing}>
          {removing ? <><Spinner animation="border" size="sm" className="me-2" />Removendo...</> : "Remover integração"}
        </Button>
      </Modal.Footer>
    </Modal>
    <Modal
      show={showTestModal}
      onHide={closeTestModal}
      centered
      scrollable
      backdrop={testBusy ? "static" : true}
      keyboard={!testBusy}
      style={{ zIndex: testModalFromTemplateEditor ? 1064 : 1062 }}
      backdropClassName={testModalFromTemplateEditor ? "nested-modal-backdrop-lv3" : "nested-modal-backdrop-lv2"}
    >
      <Modal.Header closeButton={!testBusy}>
        <Modal.Title>Enviar mensagem de teste</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id="whatsapp-system-test-form" onSubmit={sendTest}>
          <p className="small text-muted">Escolha um modelo aprovado e configurado na conta R1 e envie para um único WhatsApp pelo número padrão. A confirmação da API não garante a entrega; confira no aparelho do destinatário.</p>
          <Form.Group className="mb-3">
            <Form.Label>Modelo aprovado</Form.Label>
            <Form.Select
              required
              value={selectedTestTemplate ? testTemplateId : ""}
              onChange={(event) => {
                const templateId = event.target.value;
                const template = availableTestTemplates.find((item) => String(item.template_id) === templateId);
                setTestTemplateId(templateId);
                setTestVariables(testVariablesForTemplate(template));
                setTestResult(null);
                setTestError("");
              }}
              disabled={busy || testBusy || infrastructure?.api_enabled === false || availableTestTemplates.length === 0}
            >
              <option value="">Selecione um modelo</option>
              {availableTestTemplates.map((template) => (
                <option key={template.template_id} value={String(template.template_id)}>
                  {template.internal_title} · {template.version_language || template.language}
                </option>
              ))}
            </Form.Select>
            {availableTestTemplates.length === 0 && <Form.Text>Sincronize o catálogo da conta R1 e configure um modelo aprovado para teste. Modelos de RSVP precisam de um convite de evento e não aparecem neste teste.</Form.Text>}
          </Form.Group>
          {selectedTestTemplate && (
            <>
              <div className="border rounded bg-light p-2 mb-3 small" style={{ whiteSpace: "pre-wrap" }}>
                <div className="fw-bold mb-1">Prévia do modelo</div>
                {selectedTestTemplate.components?.header?.type === "image" && (
                  selectedTestTemplate.components.header.source === "static"
                    ? <img src={selectedTestTemplate.components.header.url} alt="Imagem do modelo" className="img-fluid rounded mb-2" style={{ maxHeight: 160 }} />
                    : <div className="border rounded p-3 text-center mb-2">QR de exemplo, sem dados de participante</div>
                )}
                {selectedTestTemplate.components?.header?.text && <div className="fw-semibold">{previewText(selectedTestTemplate.components.header.text, testVariables)}</div>}
                <div>{previewText(selectedTestTemplate.components?.body?.text, testVariables)}</div>
                {selectedTestTemplate.components?.footer?.text && <div className="text-muted">{selectedTestTemplate.components.footer.text}</div>}
                {selectedTestTemplate.components?.buttons?.map((button, index) => (
                  <div className="border-top text-primary text-center mt-2 pt-1" key={`${button.type}-${index}`}>{button.text}</div>
                ))}
              </div>
            </>
          )}
          <Form.Group>
            <Form.Label>Seu WhatsApp de destino</Form.Label>
            <InputMask
              mask="+55 (99) 99999-9999"
              maskChar={null}
              value={testRecipient}
              onChange={(event) => { setTestRecipient(event.target.value); setTestResult(null); setTestError(""); }}
              disabled={busy || testBusy || infrastructure?.api_enabled === false}
            >
              {(inputProps) => (
                <Form.Control
                  {...inputProps}
                  required
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+55 (__) _____-____"
                />
              )}
            </InputMask>
          </Form.Group>
          {testResult && <WhatsAppFeedbackAlert variant="success" className="mt-3 mb-0">{testResult.provider_status === "held_for_quality_assessment" ? "A Meta reteve o envio para análise. Confira o WhatsApp de destino mais tarde." : "A Meta aceitou a mensagem. Verifique o WhatsApp de destino."} ID do envio: {testResult.message_id}.</WhatsAppFeedbackAlert>}
          {testError && <WhatsAppFeedbackAlert variant="danger" className="mt-3 mb-0">{testError}</WhatsAppFeedbackAlert>}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="submit" form="whatsapp-system-test-form" variant="primary" disabled={busy || testBusy || !selectedTestTemplate || infrastructure?.api_enabled === false}>
          {testBusy ? <Spinner animation="border" size="sm" /> : "Enviar teste"}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default WhatsAppSystemSettingsCard;
